/**
 * useAnalytics Hook
 * React hook for easy event tracking in components
 * Automatically injects user and organization context
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { trackEvent, trackPageView } from '../services/analyticsService';

/**
 * Custom hook for analytics tracking
 * @returns {Object} - { trackEvent, trackPageView }
 */
export function useAnalytics() {
  const { user, organization } = useAppContext();
  const location = useLocation();
  
  // Debounce timers for high-frequency events
  const debounceTimers = useRef({});

  /**
   * Track an event with automatic user/org context injection
   * @param {string} eventName - The event name
   * @param {Object} properties - Event properties
   */
  const track = useCallback((eventName, properties = {}) => {
    trackEvent(eventName, properties, user, organization, location.pathname);
  }, [user, organization, location.pathname]);

  /**
   * Track a page view
   * @param {string} component - Optional component name
   */
  const trackPage = useCallback((component = null) => {
    trackPageView(location.pathname, user, organization, component);
  }, [location.pathname, user, organization]);

  /**
   * Track an event with debouncing (for high-frequency events like search)
   * @param {string} eventName - The event name
   * @param {Object} properties - Event properties
   * @param {number} delay - Debounce delay in milliseconds (default: 500)
   */
  const trackDebounced = useCallback((eventName, properties = {}, delay = 500) => {
    const key = `${eventName}_${JSON.stringify(properties)}`;
    
    // Clear existing timer
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }
    
    // Set new timer
    debounceTimers.current[key] = setTimeout(() => {
      track(eventName, properties);
      delete debounceTimers.current[key];
    }, delay);
  }, [track]);

  return {
    trackEvent: track,
    trackPageView: trackPage,
    trackDebounced,
  };
}

